import cloneDeep from 'clone-deep'
import type { InputSelect, InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { store } from 'reducers'
import { Modal } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import { dataverifyOrderableProducts, floodProductTypes, ServiceLinkOptions } from './constants'

const isDataverifyFloodVendor = (vendorKey: string) =>
  vendorKey === 'DataverifyFlood' || vendorKey === 'Dataverify'

const productOptionsForVendor = (vendorKey: string) =>
  isDataverifyFloodVendor(vendorKey) ? dataverifyOrderableProducts : floodProductTypes[vendorKey] || {}

const requestTypes: Record<string, string[]> = {
  ServiceLink: ['New Order', 'Reissue', 'Change', 'Cancellation', 'Get Status'],
  ServiceLinkWTG: ['New Order'],
  DataverifyFlood: ['New Order', 'Reissue', 'Upgrade', 'Re-determination', 'Cancellation', 'Get Status'],
  Dataverify: ['New Order', 'Reissue', 'Upgrade', 'Re-determination', 'Cancellation', 'Get Status'],
  XactusFlood: ['New Order', 'Get Status'],
}

const defaultInputs = (): Record<string, InputType> => {
  const { vendor } = store.getState()

  const vendorOptions: Record<string, string> = cloneDeep(ServiceLinkOptions)
  // Do not offer the deprecated Dataverify key for new orders; use DataverifyFlood.
  delete vendorOptions.Dataverify
  Object.keys(vendorOptions).forEach((vendorKey) => {
    if (!vendor[vendorKey]) delete vendorOptions[vendorKey]
  })

  let defaultVendor = ''
  if (Object.keys(vendorOptions).length == 1) defaultVendor = Object.keys(vendorOptions)[0]

  return {
    vendor: {
      inputType: 'ButtonGroup',
      title: 'Vendor',
      options: vendorOptions,
      value: defaultVendor,
      required: true,
      visible: !defaultVendor,
    },
    requestType: {
      inputType: 'select',
      title: 'Request Type',
      options: defaultVendor ? requestTypes[defaultVendor] : [],
      value: '',
      hasDefaultOption: true,
      required: true,
      visible: !!defaultVendor,
    },
    certificationIdentifier: {
      inputType: 'select',
      title: 'Flood Certification Identifier',
      options: [],
      value: '',
      hasDefaultOption: true,
      required: true,
      visible: false,
    },
    productType: {
      inputType: 'radio',
      title: 'Product Type',
      options: {},
      value: '',
      required: true,
      visible: false,
    },
    comment: {
      inputType: 'textarea',
      title: 'Comment',
      value: '',
      required: false,
      visible: false,
      span: 2,
    },
  }
}

const defaultProductForVendor = (vendorKey: string) => {
  if (vendorKey == 'ServiceLink') return '26'
  if (isDataverifyFloodVendor(vendorKey)) return 'LL'
  if (vendorKey == 'XactusFlood') return 'lol'
  // ServiceLinkWTG's only enabled product is 6 (Flood Life of Loan).
  return '6'
}

export function ServiceLinkOrderModal(props: {
  certificateNumbersByVendor: Record<string, string[]>
  onClose: Function
  onSubmit: Function
}) {
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs())

  const isAbleToSubmit = useMemo(() => {
    const hasProductOrCert = !!(inputs.certificationIdentifier.value || inputs.productType.value)
    if (!hasProductOrCert) return false
    if (inputs.vendor.value && isDataverifyFloodVendor(inputs.vendor.value) && inputs.requestType.value === 'Re-determination') {
      return String(inputs.comment.value || '').trim().length > 0
    }
    return true
  }, [inputs])

  const syncCertificationOptions = (newInputs: Record<string, InputType>, vendorKey: string) => {
    const nums = [
      ...(props.certificateNumbersByVendor[vendorKey] || []),
      ...(vendorKey === 'DataverifyFlood' ? props.certificateNumbersByVendor.Dataverify || [] : []),
    ].filter((n, i, arr) => arr.indexOf(n) === i)
    ;(newInputs.certificationIdentifier as InputSelect).options = nums
    if (newInputs.certificationIdentifier.value && !nums.includes(String(newInputs.certificationIdentifier.value))) {
      newInputs.certificationIdentifier.value = ''
    }
  }

  useEffect(() => {
    setInputs((prev) => {
      const next = cloneDeep(prev)
      syncCertificationOptions(next, String(next.vendor.value || ''))
      return next
    })
  }, [props.certificateNumbersByVendor])

  const onClose = () => {
    props.onClose()
  }

  const syncDataverifyComment = (newInputs: Record<string, InputType>, requestType: string, vendorKey: string) => {
    const isDv = isDataverifyFloodVendor(vendorKey)
    newInputs.comment.visible = isDv && !!requestType
    newInputs.comment.required = isDv && requestType === 'Re-determination'
    newInputs.comment.title =
      isDv && requestType === 'Re-determination' ? 'Why are you requesting Redetermination?' : 'Comment'
    if (!newInputs.comment.visible) newInputs.comment.value = ''
  }

  const syncCertificationField = (
    newInputs: Record<string, InputType>,
    vendorKey: string,
    requestType: string,
  ) => {
    const isDv = isDataverifyFloodVendor(vendorKey)
    const isNewOrder = requestType === 'New Order'
    const isReissue = isDv && requestType === 'Reissue'
    const isGetStatus = requestType === 'Get Status'

    if (isNewOrder || !requestType) {
      newInputs.certificationIdentifier.visible = false
      newInputs.certificationIdentifier.value = ''
      return
    }

    newInputs.certificationIdentifier.visible = true
    newInputs.certificationIdentifier.required = true
    newInputs.certificationIdentifier.title = 'Flood Certification Identifier'

    if (isReissue) {
      // DV: Reissue = StatusQuery with user-entered cert #
      newInputs.certificationIdentifier.inputType = 'text'
      newInputs.certificationIdentifier.type = 'text'
      ;(newInputs.certificationIdentifier as InputSelect).options = []
      ;(newInputs.certificationIdentifier as InputSelect).hasDefaultOption = false
      newInputs.certificationIdentifier.value = ''
      return
    }

    newInputs.certificationIdentifier.inputType = 'select'
    ;(newInputs.certificationIdentifier as InputSelect).hasDefaultOption = true
    syncCertificationOptions(newInputs, vendorKey)
    const nums = (newInputs.certificationIdentifier as InputSelect).options as string[]
    // DV: Get Status = StatusQuery on existing/last ordered cert
    newInputs.certificationIdentifier.value = isGetStatus && nums.length ? nums[nums.length - 1] : ''
  }

  const onChange = async (key: string, value: string) => {
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = value
    if (key == 'vendor') newInputs.requestType.visible = true
    ;(newInputs.requestType as any).options = requestTypes[newInputs.vendor.value]
    ;(newInputs.productType as any).options = productOptionsForVendor(newInputs.vendor.value)

    if (key === 'requestType') {
      const isNewOrder = value === 'New Order'
      const isUpgrade = value === 'Upgrade'
      const showProduct = isNewOrder || (isDataverifyFloodVendor(newInputs.vendor.value) && isUpgrade)
      newInputs.productType.visible = showProduct
      syncCertificationField(newInputs, newInputs.vendor.value, value)
      // Default each vendor to its Life of Loan product.
      newInputs.productType.value = showProduct ? defaultProductForVendor(newInputs.vendor.value) : ''
      syncDataverifyComment(newInputs, value, newInputs.vendor.value)
    }

    if (key === 'vendor') {
      newInputs.requestType.value = ''
      newInputs.productType.visible = false
      newInputs.productType.value = ''
      syncCertificationField(newInputs, value, '')
      syncDataverifyComment(newInputs, '', value)
    }

    setInputs(newInputs)
  }

  const onSubmit = async () => {
    const data: Record<string, any> = {}
    Object.keys(inputs).forEach((key: string) => {
      data[key] = inputs[key].value
    })

    data['certificateNumber'] = data['certificationIdentifier']
    delete data['certificationIdentifier']

    setLoading(true)
    await props.onSubmit(data)
    setLoading(false)
    props.onClose()
    return true
  }

  return (
    <Modal
      button={<span></span>}
      title={'Flood Order'}
      titleOkay="Submit Order"
      titleCancel="Close"
      loading={loading}
      isOpen
      lastUpdatedAt={Date.now()}
      disabled={!isAbleToSubmit}
      onClose={onClose}
      onOk={onSubmit}
    >
      <div className="">
        <div className="w-196">
          <div className="mb-4">
            <RenderInput input={inputs.vendor} Key={'vendor'} onChange={onChange} />
          </div>
          {inputs.requestType.visible && (
            <div className="">
              <RenderInput input={inputs.requestType} Key={'requestType'} onChange={onChange} />
            </div>
          )}
          {inputs.certificationIdentifier.visible && (
            <div className="mt-3">
              <RenderInput input={inputs.certificationIdentifier} Key={'certificationIdentifier'} onChange={onChange} />
            </div>
          )}
          {inputs.productType.visible && (
            <div className="mt-5">
              <RenderInput input={inputs.productType} Key={'productType'} onChange={onChange} />
              {inputs.productType.value && (
                <p className="bg-blue-100 text-blue-800 text-sm font-bold p-3 rounded">
                  {(floodProductTypes as any)[inputs.vendor.value][inputs.productType.value]}
                </p>
              )}
            </div>
          )}
          {inputs.comment.visible && (
            <div className="mt-3">
              <RenderInput input={inputs.comment} Key={'comment'} onChange={onChange} />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}
